import { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';

// Custom short time formatter
const formatTimeShort = (timestamp) => {
    if (!timestamp) return 'now';
    
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 5) return 'now';
    if (diffInSeconds < 60) return `${diffInSeconds}sec ago`;
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}min ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}hr ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths}mo ago`;
    
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears}y ago`;
};

const CommentDisplay = ({ comments, isAutoScrollEnabled = true }) => {
    const [visibleComments, setVisibleComments] = useState([]);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const containerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    // Handle auto-scroll and animation for new comments
    useEffect(() => {
        if (comments.length === 0) {
            setVisibleComments([]);
            return;
        }

        // Filter out any comments that might have duplicate IDs
        const uniqueComments = comments.filter((comment, index, self) => 
            index === self.findIndex(c => c.id === comment.id)
        );

        // Add new comments with animation
        const newComments = uniqueComments.slice(visibleComments.length);
        
        if (newComments.length === 0) return;
        
        newComments.forEach((comment, index) => {
            setTimeout(() => {
                setVisibleComments(prev => {
                    // Double-check for duplicates before adding
                    const exists = prev.some(c => c.id === comment.id);
                    if (exists) {
                        console.warn('🔄 Preventing duplicate comment in UI:', comment.id);
                        return prev;
                    }
                    
                    const updated = [...prev, { ...comment, isNew: true }];
                    
                    return updated;
                });

                // Auto-scroll to bottom only if user hasn't manually scrolled up
                if (!isUserScrolling && isAutoScrollEnabled && containerRef.current) {
                    const scrollToBottom = () => {
                        if (containerRef.current && !isUserScrolling) {
                            containerRef.current.scrollTop = containerRef.current.scrollHeight;
                        }
                    };
                    
                    // Single delayed scroll to bottom after comment animation
                    setTimeout(scrollToBottom, 300);
                }

                // Remove the "new" flag after animation
                setTimeout(() => {
                    setVisibleComments(prev =>
                        prev.map(c => c.id === comment.id ? { ...c, isNew: false } : c)
                    );
                }, 500);
            }, index * 200); // Stagger comment appearance
        });
    }, [comments, visibleComments.length, isUserScrolling, isAutoScrollEnabled]);

    // Only auto-scroll when user is not manually browsing old messages
    useEffect(() => {
        // Only auto-scroll if user hasn't scrolled up to see old messages
        if (!isUserScrolling && isAutoScrollEnabled && containerRef.current && visibleComments.length > 0) {
            const scrollToBottom = () => {
                if (containerRef.current && !isUserScrolling) {
                    containerRef.current.scrollTop = containerRef.current.scrollHeight;
                }
            };
            
            // Gentle scroll to bottom for new comments
            setTimeout(scrollToBottom, 200);
        }
    }, [visibleComments.length, isUserScrolling, isAutoScrollEnabled]);

    // Handle scroll detection
    const handleScroll = () => {
        if (!containerRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
        
        // If user scrolls up (away from bottom), mark as user scrolling
        if (!isAtBottom && !isUserScrolling) {
            setIsUserScrolling(true);
        }
        
        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        
        // Only reset user scrolling state if they manually scroll to bottom
        if (isAtBottom && isUserScrolling) {
            // Give a small delay to ensure they intentionally scrolled to bottom
            scrollTimeoutRef.current = setTimeout(() => {
                setIsUserScrolling(false);
            }, 100);
        }
    };

    // Update timestamps every 10 seconds
    useEffect(() => {
        const timeUpdateInterval = setInterval(() => {
            setCurrentTime(new Date());
        }, 10000); // Update every 10 seconds

        return () => clearInterval(timeUpdateInterval);
    }, []);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="relative text-white">
            {/* Comments Container */}
            <div 
                ref={containerRef}
                onScroll={handleScroll}
                className={`max-h-40 overflow-y-auto overflow-x-hidden scrollbar-none ${visibleComments.length > 5 && "pb-0"}`}
                style={{ 
                    scrollBehavior: 'auto' // Always use auto for better user control
                }}
            >
                {visibleComments.length === 0 ? (
                    <div className="text-center text-xs py-8 text-gray-400/80">
                        <p>No messages yet. Be the first to send a message!</p>
                    </div>
                ) : (
                    visibleComments.map((comment) => (
                        <CommentItem 
                            key={comment.id} 
                            comment={comment}
                            isNew={comment.isNew}
                            currentTime={currentTime}
                        />
                    ))
                )}
            </div>

            {/* Scroll to bottom button - shows when user has scrolled up */}
            {isUserScrolling && (
                <div className="absolute bottom-2 right-2 z-10">
                    <button
                        onClick={() => {
                            if (containerRef.current) {
                                // Smooth scroll to bottom
                                containerRef.current.scrollTo({
                                    top: containerRef.current.scrollHeight,
                                    behavior: 'smooth'
                                });
                                
                                // Reset scrolling state after scroll completes
                                setTimeout(() => {
                                    setIsUserScrolling(false);
                                }, 300);
                            }
                        }}
                        className="bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs hover:bg-blue-700 transition-all duration-200 shadow-lg flex items-center gap-1 animate-bounce"
                    >
                        ↓ New messages
                    </button>
                </div>
            )}

            {/* New comment indicator */}
            {/* {visibleComments.length > 0 && !isUserScrolling && (
                <div className="absolute bottom-5 right-2 text-xs text-gray-400">
                    {visibleComments.length} comment{visibleComments.length !== 1 ? 's' : ''}
                </div>
            )} */}
        </div>
    );
};

const CommentItem = ({ comment, isNew, currentTime }) => {
    // Extract user info from different possible structures
    const sender = comment.sender || {};
    const firstName = sender.first_name || comment.first_name || '';
    const lastName = sender.last_name || comment.last_name || '';
    const username = sender.username || comment.username || 'Anonymous';
    const profilePic = sender.profile_pic || comment.profile_pic;
    const isCurrentUser = comment.is_sent_by_current_user || false;
    const timestamp = comment.created_at || comment.timestamp;
    
    // Generate display name
    const displayName = firstName && lastName 
        ? `${firstName} ${lastName}` 
        : (firstName || lastName || username);
    
    // Generate avatar fallback text
    const avatarText = firstName && lastName 
        ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
        : (displayName.charAt(0).toUpperCase() || 'A');

    return (
        <div 
            className={`
                flex items-start gap-x-2 mb-2 px-2 pt-2 rounded-lg
                transform transition-all duration-500 ease-out
                ${isNew 
                    ? 'animate-fade-in-up opacity-0' 
                    : 'opacity-100'
                }
            `}
            style={{
                animation: isNew ? 'fadeInUp 0.5s ease-out forwards' : 'none'
            }}
        >
            {/* Avatar */}
            <div className="comment-avatar w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                {profilePic ? (
                    <img 
                        src={profilePic} 
                        alt={displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            // Fallback to gradient avatar if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div 
                    className={`w-full h-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm ${profilePic ? 'hidden' : 'flex'}`}
                >
                    {avatarText}
                </div>
            </div>

            {/* Comment Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className={`font-semibold text-[12px] truncate text-white`}>
                        {displayName}
                        {isCurrentUser && <span className="text-[10px] text-white ml-1">(You)</span>}
                    </span>
                    <span className="text-[10px] text-gray-400">
                        {formatTimeShort(timestamp)}
                    </span>
                </div>
                <p className={`text-[12px] break-words text-gray-200`}>
                    {comment.message}
                </p>
            </div>
        </div>
    );
};

// Add custom CSS for animations
const style = document.createElement('style');
style.textContent = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    .animate-fade-in-up {
        animation: fadeInUp 0.5s ease-out forwards;
    }

    /* Hide scrollbar styles */
    .scrollbar-none {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* Internet Explorer 10+ */
    }
    
    .scrollbar-none::-webkit-scrollbar {
        display: none; /* WebKit */
    }

    /* Profile image styles */
    .comment-avatar img {
        transition: opacity 0.2s ease-in-out;
    }
    
    .comment-avatar img:hover {
        opacity: 0.8;
    }

    /* Current user message highlight animation */
    .bg-blue-900.bg-opacity-30 {
        background-color: rgba(30, 58, 138, 0.2);
        backdrop-filter: blur(4px);
    }
`;

if (!document.head.querySelector('style[data-comment-styles]')) {
    style.setAttribute('data-comment-styles', 'true');
    document.head.appendChild(style);
}

export default CommentDisplay;